import { supabase } from '../utils/supabase';
import { triggerSessionCompletionEffects } from './sessionCompletionService';
import { filterPlayersForHostSearch } from '../utils/clubPlayerLinkDedup';

/**
 * Private Session Service
 * Handles all CRUD operations for private sessions
 */

/**
 * Create a new private session
 * @param {Object} sessionData - Session data
 * @param {string} sessionData.name - Session name
 * @param {string} sessionData.date - Session date
 * @param {string} sessionData.startTime - Start time
 * @param {string} sessionData.endTime - End time (optional)
 * @param {string} sessionData.location - Location (optional)
 * @param {string} sessionData.description - Description (optional)
 * @param {string} hostPlayerId - Host player ID
 * @returns {Promise<Object>} Created session data
 */
export const createPrivateSession = async (sessionData, hostPlayerId) => {
  try {
    const { data, error } = await supabase.rpc('create_private_session', {
      p_session_name: sessionData.name,
      p_host_player_id: hostPlayerId,
      p_session_date: sessionData.date,
      p_start_time: sessionData.startTime,
      p_end_time: sessionData.endTime || null,
      p_location: sessionData.location || null,
      p_description: sessionData.description || null
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating private session:', error);
    throw error;
  }
};

/**
 * Get private sessions for a user (as host or participant)
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of private sessions
 */
export const getPrivateSessions = async (userId) => {
  try {
    // First get sessions where user is host
    const { data: hostSessions, error: hostError } = await supabase
      .from('sessions')
      .select(`
        *,
        session_types:session_type_id (
          id,
          name,
          description
        ),
        host_player:host_player_id (
          id,
          first_name,
          last_name
        )
      `)
      .eq('host_player_id', userId)
      .eq('session_types.name', 'Private Session')
      .is('club_id', null)
      .order('session_date', { ascending: false })
      .order('start_time', { ascending: false });

    if (hostError) throw hostError;

      // Then get sessions where user is participant
      const { data: participantSessions, error: participantError } = await supabase
        .from('sessions')
        .select(`
          *,
          session_types:session_type_id (
            id,
            name,
            description
          ),
          host_player:host_player_id (
            id,
            first_name,
            last_name
          ),
          session_players!inner (
            player_id,
            status,
            joined_at
          )
        `)
        .eq('session_players.player_id', userId)
        .eq('session_types.name', 'Private Session')
        .is('club_id', null)
        .order('session_date', { ascending: false })
        .order('start_time', { ascending: false });

    if (participantError) throw participantError;

    // Combine and deduplicate
    const allSessions = [...(hostSessions || []), ...(participantSessions || [])];
    const uniqueSessions = allSessions.filter((session, index, self) => 
      index === self.findIndex(s => s.id === session.id)
    );

    return uniqueSessions;
  } catch (error) {
    console.error('Error fetching private sessions:', error);
    throw error;
  }
};

/**
 * Join a session by join code
 * @param {string} joinCode - 8-character join code
 * @param {string} playerId - Player ID joining
 * @returns {Promise<Object>} Session data
 */
export const joinSessionByCode = async (joinCode, playerId) => {
  try {
    // First, find the session by join code
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select(`
        *,
        session_types:session_type_id (
          id,
          name,
          description
        )
      `)
      .eq('join_code', joinCode)
      .eq('session_types.name', 'Private Session')
      .single();

    if (sessionError) throw sessionError;
    if (!session) throw new Error('Session not found');

    // Check if player is already in the session
    const { data: existingPlayer, error: checkError } = await supabase
      .from('session_players')
      .select('id')
      .eq('session_id', session.id)
      .eq('player_id', playerId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    if (existingPlayer) {
      throw new Error('Player is already in this session');
    }

    // Add player to session
    const { error: addError } = await supabase
      .from('session_players')
      .insert({
        session_id: session.id,
        player_id: playerId,
        status: 'signed_up',
        joined_at: new Date().toISOString()
      });

    if (addError) throw addError;

    return session;
  } catch (error) {
    console.error('Error joining session by code:', error);
    throw error;
  }
};

/**
 * Add a guest player to a private session
 * @param {string} sessionId - Session ID
 * @param {Object} guestData - Guest player data
 * @param {string} guestData.firstName - Guest first name
 * @param {string} guestData.lastName - Guest last name
 * @param {string} guestData.gender - Guest gender
 * @param {string} createdBy - Player ID who created the guest
 * @returns {Promise<Object>} Created guest player
 */
export const addGuestPlayer = async (sessionId, guestData, createdBy) => {
  try {
    // Create guest player
    const { data: guestPlayer, error: guestError } = await supabase
      .from('players')
      .insert({
        first_name: guestData.firstName,
        last_name: guestData.lastName,
        gender: guestData.gender,
        is_guest: true,
        guest_created_by: createdBy,
        auth_id: null
      })
      .select()
      .single();

    if (guestError) throw guestError;

    // Add guest to session
    const { error: sessionError } = await supabase
      .from('session_players')
      .insert({
        session_id: sessionId,
        player_id: guestPlayer.id,
        status: 'active',
        joined_at: new Date().toISOString()
      });

    if (sessionError) throw sessionError;

    return guestPlayer;
  } catch (error) {
    console.error('Error adding guest player:', error);
    throw error;
  }
};

/**
 * Add a club player to a private session
 * @param {string} sessionId - Session ID
 * @param {string} playerId - Player ID to add
 * @returns {Promise<Object>} Added player data
 */
export const addClubPlayerToSession = async (sessionId, playerId) => {
  try {
    // Check if player is already in session
    const { data: existingPlayer, error: checkError } = await supabase
      .from('session_players')
      .select('id')
      .eq('session_id', sessionId)
      .eq('player_id', playerId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    if (existingPlayer) {
      throw new Error('Player is already in this session');
    }

    // Add player to session
    const { error: addError } = await supabase
      .from('session_players')
      .insert({
        session_id: sessionId,
        player_id: playerId,
        status: 'active',
        joined_at: new Date().toISOString()
      });

    if (addError) throw addError;

    // Fetch and return player data
    const { data: player, error: playerError } = await supabase
      .from('players')
      .select('*')
      .eq('id', playerId)
      .single();

    if (playerError) throw playerError;
    return player;
  } catch (error) {
    console.error('Error adding club player to session:', error);
    throw error;
  }
};

/**
 * Add a player to session by email
 * @param {string} sessionId - Session ID
 * @param {string} email - Player email
 * @param {string} createdBy - Player ID who is adding
 * @returns {Promise<Object>} Added player data
 */
export const addPlayerByEmail = async (sessionId, email, createdBy) => {
  try {
    // Find player by email
    const { data: player, error: playerError } = await supabase
      .from('players')
      .select('*')
      .eq('email', email)
      .not('auth_id', 'is', null)
      .single();

    if (playerError) throw new Error('Player not found with this email');
    if (!player) throw new Error('Player not found with this email');

    // Add player to session
    return await addClubPlayerToSession(sessionId, player.id);
  } catch (error) {
    console.error('Error adding player by email:', error);
    throw error;
  }
};

/**
 * Remove a player from a private session
 * @param {string} sessionId - Session ID
 * @param {string} playerId - Player ID to remove
 * @param {string} hostPlayerId - Host player ID (for permission check)
 * @returns {Promise<boolean>} Success status
 */
export const removePlayer = async (sessionId, playerId, hostPlayerId) => {
  try {
    // Check if user is host
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('host_player_id')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    if (session.host_player_id !== hostPlayerId) {
      throw new Error('Only the host can remove players');
    }

    // Remove player from session
    const { error: removeError } = await supabase
      .from('session_players')
      .delete()
      .eq('session_id', sessionId)
      .eq('player_id', playerId);

    if (removeError) throw removeError;

    return true;
  } catch (error) {
    console.error('Error removing player:', error);
    throw error;
  }
};

/**
 * Get all players in a private session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} Array of session players
 */
export const getSessionPlayers = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('session_players')
      .select(`
        *,
        players:player_id (
          id,
          first_name,
          last_name,
          gender,
          is_guest,
          guest_created_by
        )
      `)
      .eq('session_id', sessionId)
      .order('joined_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching session players:', error);
    throw error;
  }
};

/**
 * End a private session
 * @param {string} sessionId - Session ID
 * @param {string} hostPlayerId - Host player ID (for permission check)
 * @returns {Promise<boolean>} Success status
 */
export const endPrivateSession = async (sessionId, hostPlayerId) => {
  try {
    // Check if user is host
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('host_player_id')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    if (session.host_player_id !== hostPlayerId) {
      throw new Error('Only the host can end the session');
    }

    // Update session status
    const { error: updateError } = await supabase
      .from('sessions')
      .update({ session_status: 'completed' })
      .eq('id', sessionId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error ending private session:', error);
    throw error;
  }
};

/**
 * Get players from clubs where user is a member
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID (to exclude already added players)
 * @returns {Promise<Array>} Array of available club players
 */
export const getAvailableClubPlayers = async (userId, sessionId) => {
  try {
    // Get user's clubs
    const { data: userClubs, error: clubsError } = await supabase
      .from('players_club')
      .select('club_id')
      .eq('player_id', userId)
      .eq('is_active', true);

    if (clubsError) throw clubsError;

    if (!userClubs || userClubs.length === 0) {
      return [];
    }

    const clubIds = userClubs.map(uc => uc.club_id);

    // Get players already in session
    const { data: sessionPlayers, error: sessionError } = await supabase
      .from('session_players')
      .select('player_id')
      .eq('session_id', sessionId);

    if (sessionError) throw sessionError;

    const sessionPlayerIds = sessionPlayers.map(sp => sp.player_id);

    // Get all players from user's clubs (both registered users and guest players)
    const { data: allPlayers, error: playersError } = await supabase
      .from('players_club')
      .select(`
        club_id,
        clubs!inner (
          id,
          name
        ),
        is_active,
        players!inner (
          id,
          first_name,
          last_name,
          gender,
          email,
          is_guest,
          is_active,
          merged_at,
          guest_invited_email
        )
      `)
      .in('club_id', clubIds)
      .eq('is_active', true)
      .order('first_name', { foreignTable: 'players' });

    if (playersError) throw playersError;

    // Extract players from the nested structure and filter out players already in session
    // For private sessions, allow the host to add themselves to courts
    const rosterPlayers = (allPlayers || [])
      .filter((pc) => {
        const player = pc.players;
        if (!player) return false;
        if (player.is_guest === true && player.merged_at != null) return false;
        if (player.is_guest === true && player.is_active === false) return false;
        return true;
      })
      .map((pc) => ({
        ...pc.players,
        club_id: pc.club_id,
        club_name: pc.clubs.name,
      }));

    const dedupedRoster = filterPlayersForHostSearch(rosterPlayers, {
      signedUpPlayerIds: new Set(sessionPlayerIds),
    });

    const availablePlayers = dedupedRoster.filter((player) => {
        // Always include the host (they can add themselves to courts)
        if (player.id === userId) {
          return true;
        }
        // Filter out other players already in session
        return !sessionPlayerIds.includes(player.id);
      });

    return availablePlayers;
  } catch (error) {
    console.error('Error fetching available club players:', error);
    throw error;
  }
};

// Update session status
export const updateSessionStatus = async (sessionId, newStatus) => {
  try {
    const { error } = await supabase.rpc('set_session_status', {
      p_session_id: sessionId,
      p_status: newStatus,
    });

    if (error) throw error;

    if (String(newStatus).toLowerCase() === 'completed') {
      triggerSessionCompletionEffects(sessionId);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating session status:', error);
    throw error;
  }
};
